import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Timer, Zap, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { shareResult } from "../lib/share";

function fmt(s) {
  const m = Math.floor((s || 0) / 60);
  const r = (s || 0) % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Endurance "New Personal Best!" celebration — fires when the player beats
 * their fastest-ever Endurance clear. Dark gym card, gold PB ribbon, big
 * time read-out, "beat your best by Ns" line, plus a shareable result card.
 * Shown ON TOP of the end dialog; closing it reveals the end dialog.
 *
 * Props: open, data {timeSec, prevBest, mile, coins, calories, selectedAvatar}, onClose
 */
export default function EnduranceBestCelebration({ open, data, onClose }) {
  const { t } = useTranslation();
  const [sharing, setSharing] = useState(false);
  const onCloseRef = React.useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const improvedBy =
    data && data.prevBest != null && data.timeSec != null
      ? Math.max(0, data.prevBest - data.timeSec)
      : 0;

  const handleShare = async () => {
    if (!data) return;
    setSharing(true);
    try {
      await shareResult({
        title: "Fitjong",
        text: `Fitjong Endurance · new personal best ${fmt(data.timeSec)} on Mile ${data.mile}`,
        card: {
          won: true,
          timeSec: data.timeSec,
          coins: data.coins || 0,
          calories: data.calories || 0,
          worldTitle: t("endurance.pb.share_tag", "New Personal Best"),
          levelName: t("endurance.mile_n", { n: data.mile }),
          selectedAvatar: data.selectedAvatar || null,
        },
      });
    } finally {
      setSharing(false);
    }
  };

  return (
    <AnimatePresence>
      {open && data && (
        <div
          data-testid="endurance-best-celebration"
          className="fixed inset-0 z-[215] flex items-center justify-center px-4 bg-[#191d16]/70 backdrop-blur-[3px]"
          aria-live="polite"
        >
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="relative w-full max-w-[350px] rounded-3xl overflow-hidden shadow-2xl text-center px-6 pt-7 pb-6"
            style={{
              background: "linear-gradient(160deg,#3A473B 0%,#232a20 100%)",
              boxShadow: "0 24px 70px rgba(15,18,13,0.7), inset 0 2px 0 rgba(255,255,255,0.16)",
            }}
          >
            {/* sparkles */}
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                animate={{
                  opacity: [0, 1, 0],
                  x: (i % 2 === 0 ? -1 : 1) * (34 + i * 15),
                  y: -12 - i * 12,
                  scale: 1,
                  rotate: 28 * (i - 2),
                }}
                transition={{ duration: 1.7, delay: 0.2 + i * 0.12, repeat: Infinity, repeatDelay: 0.6 }}
                className="absolute top-4 left-1/2 -translate-x-1/2 text-[#E8A365] pointer-events-none"
              >
                <Sparkles size={16} strokeWidth={2.2} />
              </motion.div>
            ))}

            {/* Gold PB ribbon */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 320, damping: 16 }}
              className="relative inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full mb-4"
              style={{
                background: "linear-gradient(135deg,#F6C56B 0%,#E8A365 55%,#C9832F 100%)",
                boxShadow: "0 6px 18px rgba(201,131,47,0.5)",
              }}
            >
              <Zap size={14} strokeWidth={2.6} className="text-[#2C332A]" />
              <span className="text-[12px] uppercase tracking-[0.18em] font-extrabold text-[#2C332A]">
                {t("endurance.pb.tag", "New Personal Best!")}
              </span>
            </motion.div>

            <p className="relative text-xs text-white/60 mb-1">
              {t("endurance.mile_n", { n: data.mile })} · {t("endurance.pb.first_line", "Fastest Endurance clear yet")}
            </p>

            {/* Big time */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25, type: "spring", stiffness: 260, damping: 18 }}
              className="relative flex items-center justify-center gap-2 my-2"
            >
              <Timer size={30} strokeWidth={2} className="text-[#E8A365]" />
              <span
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-6xl font-light text-white tabular-nums leading-none"
              >
                {fmt(data.timeSec)}
              </span>
            </motion.div>

            {improvedBy > 0 && (
              <p className="relative text-sm text-[#9CB39E] font-semibold mt-1">
                {t("endurance.pb.beat_by", { n: improvedBy })}
                <span className="text-white/45 font-normal">
                  {" "}({t("endurance.best_time", "Best clear")}: {fmt(data.prevBest)})
                </span>
              </p>
            )}

            {/* Actions */}
            <div className="relative mt-6 flex flex-col gap-2.5">
              <button
                data-testid="endurance-best-share-btn"
                onClick={handleShare}
                disabled={sharing}
                className="w-full h-12 rounded-2xl font-bold text-[#2C332A] inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform"
                style={{ background: "linear-gradient(135deg,#F6C56B 0%,#E8A365 60%,#D68B3A 100%)" }}
              >
                <Share2 size={18} strokeWidth={2.4} />
                {t("endurance.pb.share", "Share result")}
              </button>
              <button
                data-testid="endurance-best-close-btn"
                onClick={() => onCloseRef.current?.()}
                className="w-full h-11 rounded-2xl font-semibold text-white/90 bg-white/10 hover:bg-white/15 active:scale-[0.98] transition-all"
              >
                {t("endurance.pb.keep_going", "Keep going")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
